import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { List, ListType } from '@cleverlist/types';
import { Button, Input } from '@cleverlist/ui';
import { useApi } from '../providers/ApiProvider';

const LIST_TYPE_ICONS: Record<ListType, string> = {
  grocery: '🛒',
  media: '🎬',
  task: '✅',
  gift: '🎁',
  place: '📍',
  custom: '📝',
  recipe: '🍳',
};

export function ListsPage() {
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListType, setNewListType] = useState<ListType>('grocery');

  const api = useApi();
  const queryClient = useQueryClient();

  const { data: lists, isLoading } = useQuery({
    queryKey: ['lists'],
    queryFn: () => api.getLists(),
  });

  const myLists = lists?.filter(
    (l) => l.current_user_role === 'owner' || !l.current_user_role
  ) ?? [];
  const sharedLists = lists?.filter(
    (l) => l.current_user_role && l.current_user_role !== 'owner'
  ) ?? [];

  const createListMutation = useMutation({
    mutationFn: (data: { name: string; type: ListType }) => api.createList(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      setShowNewList(false);
      setNewListName('');
      setNewListType('grocery');
    },
  });

  const deleteListMutation = useMutation({
    mutationFn: (id: number) => api.deleteList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
    },
  });

  const handleCreateList = (e: React.FormEvent) => {
    e.preventDefault();
    if (newListName.trim()) {
      createListMutation.mutate({ name: newListName.trim(), type: newListType });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Lists</h1>
        <Button onClick={() => setShowNewList(true)}>New List</Button>
      </div>

      {showNewList && (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <form onSubmit={handleCreateList} className="space-y-4">
            <Input
              label="List name"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              placeholder="e.g., Weekly Groceries"
              autoFocus
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                List type
              </label>
              <select
                value={newListType}
                onChange={(e) => setNewListType(e.target.value as ListType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="grocery">🛒 Grocery</option>
                <option value="media">🎬 Media (Movies, TV, Books)</option>
                <option value="task">✅ Tasks</option>
                <option value="gift">🎁 Gifts</option>
                <option value="place">📍 Places</option>
                <option value="custom">📝 Custom</option>
                <option value="recipe">🍳 Recipes</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" loading={createListMutation.isPending}>
                Create List
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowNewList(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {myLists.length === 0 && sharedLists.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <p className="text-gray-500 mb-4">You don't have any lists yet.</p>
          <Button onClick={() => setShowNewList(true)}>Create your first list</Button>
        </div>
      ) : (
        <>
          {myLists.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {myLists.map((list: List) => (
                <Link
                  key={list.id}
                  to={`/lists/${list.id}`}
                  className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{LIST_TYPE_ICONS[list.type]}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600">
                          {list.name}
                        </h3>
                        <p className="text-sm text-gray-500 capitalize">{list.type}</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        if (confirm('Delete this list?')) {
                          deleteListMutation.mutate(list.id);
                        }
                      }}
                      className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {sharedLists.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Shared Lists</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {sharedLists.map((list: List) => (
                  <Link
                    key={list.id}
                    to={`/lists/${list.id}`}
                    className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{LIST_TYPE_ICONS[list.type]}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900 group-hover:text-blue-600">
                          {list.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {list.owner?.name ?? 'Someone'} shared this list with you
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
